import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BackHeader } from "@/components/TopBar";
import { Spinner } from "@/components/common";
import { toast } from "sonner";
import { Users, DoorOpen, MessageCircle, UserPlus, Flag, Trash2, ShieldOff, ShieldCheck } from "lucide-react";

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [reports, setReports] = useState([]);

  useEffect(() => {
    if (user && user.role !== "admin") { navigate("/home"); return; }
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/listings").then((r) => setListings(r.data)).catch(() => {});
    api.get("/admin/reports").then((r) => setReports(r.data)).catch(() => {});
  }, [user]);

  const suspend = async (uid) => {
    const { data } = await api.post(`/admin/users/${uid}/suspend`);
    toast(data.suspended ? "User suspended" : "User reinstated");
    setUsers((s) => s.map((u) => (u.user_id === uid ? { ...u, suspended: data.suspended } : u)));
  };
  const remove = async (lid) => {
    await api.post(`/admin/listings/${lid}/remove`);
    toast("Listing removed");
    setListings((s) => s.map((l) => (l.listing_id === lid ? { ...l, status: "removed" } : l)));
  };

  const tabs = [["overview", "Overview"], ["users", "Users"], ["listings", "Listings"], ["reports", "Reports"]];

  const statCards = stats ? [
    { label: "Total users", value: stats.total_users, icon: Users },
    { label: "Active listings", value: stats.active_listings, icon: DoorOpen },
    { label: "Conversations", value: stats.active_conversations, icon: MessageCircle },
    { label: "New signups (7d)", value: stats.new_signups, icon: UserPlus },
    { label: "Open reports", value: stats.open_reports, icon: Flag },
  ] : [];

  return (
    <div className="pb-10">
      <BackHeader title="Admin dashboard" />
      <div className="px-4 pt-4">
        <div className="no-scrollbar mb-4 flex gap-2 overflow-x-auto">
          {tabs.map(([id, label]) => (
            <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)} className={`shrink-0 rounded-full px-4 py-2 text-sm font-bold transition-colors ${tab === id ? "bg-brand text-white" : "bg-white text-ink-soft shadow-soft"}`}>{label}</button>
          ))}
        </div>

        {tab === "overview" && (!stats ? <Spinner /> : (
          <div className="grid grid-cols-2 gap-3">
            {statCards.map((c) => (
              <div key={c.label} className="rounded-3xl bg-white p-4 shadow-card">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-brand-light"><c.icon className="h-5 w-5 text-brand" /></div>
                <p className="mt-3 font-heading text-3xl font-extrabold text-ink">{c.value}</p>
                <p className="text-xs text-ink-soft">{c.label}</p>
              </div>
            ))}
          </div>
        ))}

        {tab === "users" && (
          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.user_id} className="flex items-center gap-3 rounded-2xl bg-white p-3 shadow-soft">
                <img src={resolveImg(u.profile?.photo_url) || "https://images.unsplash.com/photo-1625241152315-4a698f74ceb7?w=100"} alt="" className="h-10 w-10 rounded-full object-cover" />
                <div className="min-w-0 flex-1"><p className="truncate font-semibold text-ink">{u.name} {u.role === "admin" && <span className="text-xs text-brand">· admin</span>}</p><p className="truncate text-xs text-ink-soft">{u.email}</p></div>
                {u.role !== "admin" && (
                  <button data-testid={`suspend-${u.user_id}`} onClick={() => suspend(u.user_id)} className={`rounded-full px-3 py-1.5 text-xs font-bold ${u.suspended ? "bg-mint/15 text-mint" : "bg-destructive/10 text-destructive"}`}>
                    {u.suspended ? <ShieldCheck className="h-4 w-4" /> : <ShieldOff className="h-4 w-4" />}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "listings" && (
          <div className="space-y-2">
            {listings.map((l) => (
              <div key={l.listing_id} className="flex items-center gap-3 rounded-2xl bg-white p-3 shadow-soft">
                <img src={resolveImg(l.images?.[0]) || "https://images.unsplash.com/photo-1662454419716-c4c504728811?w=100"} alt="" className="h-12 w-12 rounded-xl object-cover" />
                <div className="min-w-0 flex-1"><p className="truncate font-semibold text-ink">{l.title}</p><p className="truncate text-xs text-ink-soft">{l.locality}, {l.city} · <span className={l.status === "removed" ? "text-destructive" : "text-mint"}>{l.status}</span></p></div>
                {l.status !== "removed" && <button data-testid={`remove-${l.listing_id}`} onClick={() => remove(l.listing_id)} className="grid h-9 w-9 place-items-center rounded-full bg-destructive/10 text-destructive"><Trash2 className="h-4 w-4" /></button>}
              </div>
            ))}
          </div>
        )}

        {tab === "reports" && (
          reports.length ? <div className="space-y-2">
            {reports.map((r) => (
              <div key={r.report_id} className="rounded-2xl bg-white p-4 shadow-soft">
                <div className="flex items-center gap-2 text-sm font-semibold text-ink"><Flag className="h-4 w-4 text-destructive" /> {r.target_type} report</div>
                <p className="mt-1 text-sm text-ink-soft">{r.reason}</p>
              </div>
            ))}
          </div> : <p className="py-10 text-center text-ink-soft">No reports 🎉</p>
        )}
      </div>
    </div>
  );
}
